import { initializeApp } from 'firebase/app'
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail,
  onAuthStateChanged,
  reauthenticateWithCredential,
  EmailAuthProvider,
  updatePassword,
  verifyBeforeUpdateEmail,
  type User,
} from 'firebase/auth'
import {
  initializeFirestore,
  doc,
  setDoc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  deleteDoc,
  increment,
} from 'firebase/firestore'

const firebaseConfig = {
  apiKey: 'AIzaSyAjpYThNrNWDdcKp6N8mn1m4B52Ty__NnE',
  authDomain: 'shop-16968.firebaseapp.com',
  projectId: 'shop-16968',
  storageBucket: 'shop-16968.firebasestorage.app',
  messagingSenderId: '1057983305014',
  appId: '1:1057983305014:web:4e63c75557b7c47bcbcf97',
  measurementId: 'G-4M5D03LZMT',
}

const app = initializeApp(firebaseConfig)
export const auth = getAuth(app)
// Initialize Firestore with long-polling forced ON instead of the default
// WebChannel streaming transport. The default streaming Watch transport is
// fragile inside sandboxed iframe / CDN-bundled environments and, under the
// rapid subscribe/unsubscribe churn of React 18 StrictMode double-mounting
// (orders + cart + sales + review listeners), corrupts the stream's internal
// target version state — surfacing as:
//   "FIRESTORE INTERNAL ASSERTION FAILED: Unexpected state (ID: b815)"
//   with nested (ID: ca9) {"ve":-1}.
// Forcing long-polling uses a request/response transport that does not hold a
// long-lived stream, which sidesteps that corrupted-watch-stream assertion.
export const db = initializeFirestore(app, {
  experimentalForceLongPolling: true,
})

// ─── Types ───────────────────────────────────────────────

export interface Address {
  id: string
  recipientName: string
  line1: string
  line2?: string
  city: string
  state: string
  postalCode: string
  country: string
  isDefault?: boolean
  deliveryInstructions?: string
}

export interface CustomerProfile {
  email: string
  displayName: string
  createdAt: string
  marketingOptIn?: boolean
  addresses?: Address[]
}

export interface OrderLineItem {
  productId: string
  name: string
  quantity: number
  unitPrice: number // in cents
  type: 'book' | 'supplement'
}

export interface Order {
  id: string
  userUid: string
  email: string
  stripeSessionId: string
  lineItems: OrderLineItem[]
  subtotal: number // in cents
  total: number // in cents
  status: 'paid' | 'refunded' | 'pending'
  fulfillmentStatus: 'pending' | 'shipped' | 'delivered' | 'digital_delivered'
  shippingAddress?: {
    line1: string
    line2?: string
    city: string
    state: string
    postalCode: string
    country: string
  }
  trackingNumber?: string
  trackingCarrier?: string
  downloadUrl?: string // for digital books
  createdAt: string
  updatedAt: string
}

// ─── Auth Functions ───────────────────────────────────────────────

/**
 * Create a new customer account with Firebase Auth + Firestore profile.
 */
export async function signUp(
  email: string,
  password: string,
  displayName: string,
  marketingOptIn = false,
): Promise<{ uid: string; email: string }> {
  const credential = await createUserWithEmailAndPassword(auth, email, password)
  const user = credential.user

  await updateProfile(user, { displayName })

  const profile: CustomerProfile = {
    email: email.trim().toLowerCase(),
    displayName,
    createdAt: new Date().toISOString(),
    marketingOptIn,
  }

  await setDoc(doc(db, 'customers', user.uid), profile)

  // Send verification email (non-blocking — don't fail signup if this errors)
  try {
    await sendEmailVerification(user)
  } catch {
    // ignore — user can request resend later
  }

  return { uid: user.uid, email: profile.email }
}

/**
 * Sign in an existing customer.
 */
export async function signIn(
  email: string,
  password: string,
): Promise<{ uid: string; email: string }> {
  const credential = await signInWithEmailAndPassword(auth, email, password)
  const user = credential.user
  return { uid: user.uid, email: user.email ?? email }
}

/**
 * Sign out the current user.
 */
export async function signOutUser(): Promise<void> {
  await signOut(auth)
}

/**
 * Send a password reset email.
 */
export async function sendPasswordReset(email: string): Promise<void> {
  await sendPasswordResetEmail(auth, email)
}

/**
 * Resend verification email to the currently signed-in user.
 */
export async function resendVerificationEmail(): Promise<void> {
  const user = auth.currentUser
  if (user && !user.emailVerified) {
    await sendEmailVerification(user)
  }
}

/**
 * Update the signed-in user's display name (Firebase Auth + Firestore profile).
 * No reauthentication required.
 */
export async function updateDisplayName(name: string): Promise<void> {
  const user = auth.currentUser
  if (!user) throw new Error('Not signed in.')
  const trimmed = name.trim()
  if (!trimmed) throw new Error('Please enter your name.')
  await updateProfile(user, { displayName: trimmed })
  await updateCustomerProfile(user.uid, { displayName: trimmed })
}

/**
 * Change the signed-in user's email address. Firebase requires a recent
 * login, so the current password is used to reauthenticate first. A
 * verification email is sent to the NEW address; the change takes effect
 * once the user clicks that link.
 */
export async function updateUserEmail(
  newEmail: string,
  currentPassword: string,
): Promise<void> {
  const user = auth.currentUser
  if (!user || !user.email) throw new Error('Not signed in.')
  const clean = newEmail.trim().toLowerCase()
  if (!clean.includes('@')) throw new Error('Please enter a valid email.')
  const credential = EmailAuthProvider.credential(user.email, currentPassword)
  await reauthenticateWithCredential(user, credential)
  await verifyBeforeUpdateEmail(user, clean)
  // Reflect the pending email in Firestore so the profile stays in sync.
  await updateCustomerProfile(user.uid, { email: clean })
}

/**
 * Change the signed-in user's password. Reauthenticates with the current
 * password first (Firebase requires a recent login for this operation).
 */
export async function changeUserPassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  const user = auth.currentUser
  if (!user || !user.email) throw new Error('Not signed in.')
  if (newPassword.length < 6)
    throw new Error('Password must be at least 6 characters.')
  const credential = EmailAuthProvider.credential(user.email, currentPassword)
  await reauthenticateWithCredential(user, credential)
  await updatePassword(user, newPassword)
}

/**
 * Read a customer profile from Firestore.
 */
export async function getCustomerProfile(
  uid: string,
): Promise<CustomerProfile | null> {
  const snap = await getDoc(doc(db, 'customers', uid))
  return snap.exists() ? (snap.data() as CustomerProfile) : null
}

/**
 * Update a customer profile in Firestore (merges with existing data).
 */
export async function updateCustomerProfile(
  uid: string,
  data: Partial<CustomerProfile>,
): Promise<void> {
  await setDoc(doc(db, 'customers', uid), data, { merge: true })
}

/**
 * Subscribe to a user's auth state changes.
 * Returns the unsubscribe function.
 */
export function subscribeToAuth(
  callback: (user: User | null) => void,
): () => void {
  return onAuthStateChanged(auth, callback)
}

// ─── Order Functions ───────────────────────────────────────────────

/**
 * Subscribe to a customer's orders, sorted newest first.
 * Returns the unsubscribe function.
 */
export function subscribeToOrders(
  uid: string,
  callback: (orders: Order[]) => void,
): () => void {
  const q = query(
    collection(db, 'orders'),
    where('userUid', '==', uid),
    orderBy('createdAt', 'desc'),
  )
  return onSnapshot(
    q,
    (snap) => {
      const orders = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as Order[]
      callback(orders)
    },
    (err) => {
      // If the composite index doesn't exist yet, log so dev sees it
      console.error('[Firebase] subscribeToOrders error:', err)
      callback([])
    },
  )
}

/**
 * One-time fetch of a customer's orders (alternative to subscription).
 */
export async function getOrder(orderId: string): Promise<Order | null> {
  const snap = await getDoc(doc(db, 'orders', orderId))
  return snap.exists() ? ({ id: snap.id, ...snap.data() } as Order) : null
}

// ─── Sales Analytics (automatic best sellers) ─────────────────────────────
//
// A single aggregate document tallies how many units of each product have
// sold. The storefront reads this to automatically tag the top sellers — no
// hand-maintained "best seller" list required.
//
// Stored at: analytics/salesByProduct
//   { counts: { [productId]: number }, updatedAt: string }
//
// In production the authoritative writer is the Stripe webhook (server side),
// which increments these counts when a payment is confirmed. `recordProductSales`
// below is the client-side bridge so the system also accumulates sales in this
// app immediately after a successful checkout.
//
// IMPORTANT: Your Firestore rules must allow public read on this doc so the
// shop can rank sellers, and allow the increment writes you intend to use:
//
//   match /analytics/salesByProduct {
//     allow read: if true;
//     allow write: if request.auth != null;
//   }

export interface SalesByProduct {
  counts: Record<string, number>
  updatedAt: string
}

/**
 * Real-time subscription to the per-product sales tally used to rank best
 * sellers. Fires immediately with the current counts, then on every change.
 * Returns an empty map when the doc doesn't exist yet (brand-new site).
 */
export function subscribeToSalesByProduct(
  callback: (counts: Record<string, number>) => void,
): () => void {
  return onSnapshot(
    doc(db, 'analytics', 'salesByProduct'),
    (snap) => {
      if (!snap.exists()) {
        callback({})
        return
      }
      const data = snap.data() as SalesByProduct
      callback(
        data.counts && typeof data.counts === 'object' ? data.counts : {},
      )
    },
    (err) => {
      console.error('[Firebase] subscribeToSalesByProduct error:', err)
      callback({})
    },
  )
}

/**
 * Increment the sales tally for the given purchased items. Called after a
 * confirmed checkout so best-seller ranking reflects real demand. Each entry
 * bumps `counts.<productId>` by its quantity using an atomic Firestore
 * increment, so concurrent purchases never clobber each other.
 */
export async function recordProductSales(
  items: { productId: string; quantity: number }[],
): Promise<void> {
  if (!items.length) return
  const payload: Record<string, unknown> = {
    updatedAt: new Date().toISOString(),
  }
  for (const { productId, quantity } of items) {
    if (!productId || quantity <= 0) continue
    payload[`counts.${productId}`] = increment(quantity)
  }
  await setDoc(doc(db, 'analytics', 'salesByProduct'), payload, { merge: true })
}

// ─── Newsletter Functions ───────────────────────────────────────────────

/**
 * Add an email to the newsletter signup list.
 * Uses the lowercased email as the doc ID so re-submissions are idempotent.
 *
 * Stored at: newsletter_signups/{email}
 *
 * IMPORTANT: Your Firestore rules must allow public create on this
 * collection. Add a rule like:
 *
 *   match /newsletter_signups/{email} {
 *     allow create: if request.resource.data.keys().hasOnly(
 *       ['email', 'source', 'createdAt']
 *     );
 *   }
 */
export async function subscribeToNewsletter(
  email: string,
  source: string = 'unknown',
): Promise<void> {
  const cleanEmail = email.trim().toLowerCase()
  if (!cleanEmail || !cleanEmail.includes('@')) {
    throw new Error('Please enter a valid email address.')
  }
  await setDoc(
    doc(db, 'newsletter_signups', cleanEmail),
    {
      email: cleanEmail,
      source,
      createdAt: new Date().toISOString(),
    },
    { merge: true },
  )
}

// ─── Cart Functions ───────────────────────────────────────────────

/**
 * Persisted cart shape stored at `carts/{uid}`. Items are intentionally
 * a minimal serializable list — the React layer rehydrates derived state.
 */
export interface PersistedCartItem {
  productId: string
  name: string
  unitPrice: number // in cents
  quantity: number
  type: 'book' | 'supplement'
  stripePriceId?: string
  lookupKey?: string
  accent?: string
}

export interface PersistedCart {
  items: PersistedCartItem[]
  updatedAt: string
}

/**
 * One-time fetch of a user's cart from Firestore.
 * Returns an empty array if no cart doc exists yet.
 */
export async function getUserCart(uid: string): Promise<PersistedCartItem[]> {
  const snap = await getDoc(doc(db, 'carts', uid))
  if (!snap.exists()) return []
  const data = snap.data() as PersistedCart
  return Array.isArray(data.items) ? data.items : []
}

/**
 * Write the user's cart to Firestore. Replaces any prior cart entirely.
 */
export async function setUserCart(
  uid: string,
  items: PersistedCartItem[],
): Promise<void> {
  await setDoc(doc(db, 'carts', uid), {
    items,
    updatedAt: new Date().toISOString(),
  })
}

/**
 * Real-time subscription to a user's cart.
 * Fires immediately with the current cart, then on every change.
 */
export function subscribeToUserCart(
  uid: string,
  callback: (items: PersistedCartItem[]) => void,
): () => void {
  return onSnapshot(
    doc(db, 'carts', uid),
    (snap) => {
      // Ignore snapshots that originate from our own un-acknowledged local
      // writes. These "echo" snapshots can arrive interleaved with rapid
      // cart edits and would otherwise stomp the freshly-added item back to
      // an older server state. We only act on server-confirmed snapshots.
      if (snap.metadata.hasPendingWrites) return
      if (!snap.exists()) {
        callback([])
        return
      }
      const data = snap.data() as PersistedCart
      callback(Array.isArray(data.items) ? data.items : [])
    },
    (err) => {
      console.error('[Firebase] subscribeToUserCart error:', err)
      callback([])
    },
  )
}

/**
 * Clear the user's cart in Firestore (used after a real Stripe checkout success).
 */
export async function clearUserCart(uid: string): Promise<void> {
  await deleteDoc(doc(db, 'carts', uid))
}

// ─── Reviews (verified-purchase gated) ────────────────────────────────────
//
// Only customers who have actually bought a product can review it. The data
// model already proves purchase: an order in `orders` with the buyer's
// `userUid`, `status: 'paid'`, and the product in its `lineItems`.
//
// A review is stored at `reviews/{productId}_{uid}` — a deterministic id that
// enforces ONE review per product per customer (re-submitting edits theirs).
//
// IMPORTANT: Firestore security rules are the real gate (client checks are
// just UX). Recommended rules:
//
//   match /reviews/{reviewId} {
//     allow read: if true;
//     allow create, update: if request.auth != null
//       && request.resource.data.userUid == request.auth.uid
//       && request.resource.data.rating is number
//       && request.resource.data.rating >= 1
//       && request.resource.data.rating <= 5
//       && exists(/databases/$(database)/documents/orders/$(request.resource.data.orderId))
//       && get(/databases/$(database)/documents/orders/$(request.resource.data.orderId)).data.userUid == request.auth.uid
//       && get(/databases/$(database)/documents/orders/$(request.resource.data.orderId)).data.status == 'paid';
//   }
//   match /analytics/reviewStats {
//     allow read: if true;
//     allow write: if request.auth != null;
//   }
//
// (The rule verifies ownership + paid status via the referenced order. The
// client additionally verifies the product is actually in that order's line
// items before allowing the form, which rules can't easily check on arrays.)

export interface Review {
  id: string
  productId: string
  userUid: string
  displayName: string
  rating: number // 1..5
  title: string
  body: string
  orderId: string
  verifiedPurchase: boolean
  createdAt: string
  updatedAt: string
}

/**
 * Returns the id of a PAID order belonging to `uid` that contains `productId`,
 * or null if the customer hasn't purchased it. This is the proof-of-purchase
 * used to gate reviewing.
 */
export async function findPurchaseOrderId(
  uid: string,
  productId: string,
): Promise<string | null> {
  const q = query(
    collection(db, 'orders'),
    where('userUid', '==', uid),
    where('status', '==', 'paid'),
  )
  const snap = await getDocs(q)
  for (const d of snap.docs) {
    const order = d.data() as Order
    if (order.lineItems?.some((li) => li.productId === productId)) {
      return d.id
    }
  }
  return null
}

/**
 * Real-time list of reviews for a product, newest first.
 */
export function subscribeToProductReviews(
  productId: string,
  callback: (reviews: Review[]) => void,
): () => void {
  const q = query(
    collection(db, 'reviews'),
    where('productId', '==', productId),
    orderBy('createdAt', 'desc'),
  )
  return onSnapshot(
    q,
    (snap) => {
      const reviews = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as Review[]
      callback(reviews)
    },
    (err) => {
      console.error('[Firebase] subscribeToProductReviews error:', err)
      callback([])
    },
  )
}

/**
 * Fetch the current user's existing review for a product (if any) so the form
 * can prefill / show "you've already reviewed this".
 */
export async function getUserReviewForProduct(
  productId: string,
  uid: string,
): Promise<Review | null> {
  const snap = await getDoc(doc(db, 'reviews', `${productId}_${uid}`))
  return snap.exists() ? ({ id: snap.id, ...snap.data() } as Review) : null
}

/**
 * Create or update the signed-in customer's review for a product. The caller
 * must pass a valid `orderId` proving purchase (from `findPurchaseOrderId`).
 * Also keeps the `analytics/reviewStats` aggregate in sync so product cards
 * can show an average rating + count without loading every review.
 */
export async function submitReview(input: {
  productId: string
  userUid: string
  displayName: string
  rating: number
  title: string
  body: string
  orderId: string
}): Promise<void> {
  const { productId, userUid, rating } = input
  if (rating < 1 || rating > 5) throw new Error('Rating must be 1–5 stars.')

  const reviewId = `${productId}_${userUid}`
  const ref = doc(db, 'reviews', reviewId)

  // Read the prior review (if any) to compute aggregate deltas.
  const prior = await getDoc(ref)
  const priorRating = prior.exists() ? (prior.data() as Review).rating : null
  const priorCreatedAt = prior.exists()
    ? (prior.data() as Review).createdAt
    : null

  const now = new Date().toISOString()
  const review: Omit<Review, 'id'> = {
    productId,
    userUid,
    displayName: input.displayName,
    rating,
    title: input.title,
    body: input.body,
    orderId: input.orderId,
    verifiedPurchase: true,
    createdAt: priorCreatedAt ?? now,
    updatedAt: now,
  }
  await setDoc(ref, review)

  // Update the rolling aggregate: sum of ratings + count of reviews.
  const sumDelta = priorRating === null ? rating : rating - priorRating
  const countDelta = priorRating === null ? 1 : 0
  await setDoc(
    doc(db, 'analytics', 'reviewStats'),
    {
      stats: {
        [productId]: {
          sum: increment(sumDelta),
          count: increment(countDelta),
        },
      },
      updatedAt: now,
    },
    { merge: true },
  )
}

export interface ReviewStat {
  sum: number
  count: number
}

/**
 * Real-time subscription to per-product review aggregates (sum + count of
 * ratings). Returns an empty map when no reviews exist yet.
 */
export function subscribeToReviewStats(
  callback: (stats: Record<string, ReviewStat>) => void,
): () => void {
  return onSnapshot(
    doc(db, 'analytics', 'reviewStats'),
    (snap) => {
      if (!snap.exists()) {
        callback({})
        return
      }
      const data = snap.data() as { stats?: Record<string, ReviewStat> }
      callback(data.stats && typeof data.stats === 'object' ? data.stats : {})
    },
    (err) => {
      console.error('[Firebase] subscribeToReviewStats error:', err)
      callback({})
    },
  )
}

export type { User }
